import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { formatDatetimeForTable } from '../../../../lib/table-utils';
import { useFetchOrgActiveRuns } from '@/api/queries/analyticsQueries/useFetchOrgRuns';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { usePlatformPermissionCheck } from '@/hooks/usePlatformPermissionCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { CellWithCopy } from '@/new/components/table/table-cell-with-copy';
import { Run } from '@/types';

const fallbackTableData: Run[] = [];

const columnHelper = createColumnHelper<Run>();
const columns = [
  columnHelper.accessor('id', {
    header: () => <span>Run Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-42 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('caseId', {
    header: () => <span>Case Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-42 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('status', {
    header: () => <span>Status</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('createdBy.email', {
    header: () => <span>Created By</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('createdDate', {
    header: () => <span>Date Created</span>,
    cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
  }),
];

export function OrganisationRuns() {
  const { organisationId } = useParams();
  if (!organisationId) return null;

  usePlatformPermissionCheck(Permission.PlatformManageRuns);

  const { data: organisation } = useGetOrganisation(organisationId);
  const { data: runsData } = useFetchOrgActiveRuns(organisationId);

  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: runsData ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <Breadcrumb
        items={
          organisation
            ? [
                { href: '/admin/organisations', label: 'Admin', active: false },
                {
                  href: `/admin/organisations/${organisation.id}`,
                  label: organisation.name,
                  active: false,
                },
                {
                  href: `/admin/organisations/${organisation.id}/runs`,
                  label: 'Runs',
                  active: true,
                },
              ]
            : [{ href: '/admin/organisations', label: 'Admin', active: false }]
        }
      />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Runs" />
        </PageHeader>
      </PageContainer>

      <ScrollableTable table={table} plural="runs" globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
    </>
  );
}
